// SwitcherToggle.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useBase } from 'context/BaseContext';
import useBaseVocabulary from 'hooks/useBaseVocabulary';

// Utilities
import { isPublicBase } from 'utilities/baseVisibility';
import { PhotoShape } from 'utilities/Enums';
import { openBasePlanSettings, subscribeOpenBasePlanSettings } from 'utilities/baseLimitFeedback';
import { subscribeOpenBaseInvite } from 'utilities/openBaseInvite';
import { openBaseSettings, subscribeOpenBaseSettings } from 'utilities/openBaseSettings';

// CSS
import './SwitcherToggle.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import BaseCover from 'components/basecover/BaseCover';
import InviteModal from 'main/sidebar/switcher/popup/members/invite/InviteModal';
import SettingsModal from 'main/sidebar/switcher/popup/settings/SettingsModal';
import SwitcherPopup from 'main/sidebar/switcher/popup/SwitcherPopup';

const SETTINGS_TAB_PLAN = 'plan';

/**
 * SwitcherToggle
 *
 * Renders the toggle component for the sidebar switcher.
 *
 * @param {function} onClick - Click handler for the toggle.
 *
 * @returns {ReactNode} - SwitcherToggle.
 */
const SwitcherToggle = React.memo(({
  onClick,
  onBaseSelect,
  onBaseAdd,
  bases = [],
  activeBaseKey = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { isSidebarRailCompact } = useUI();
  const { activeBase } = useBase();
  const { baseLabel } = useBaseVocabulary();
  const compact = Boolean(isSidebarRailCompact);

  // ----- STATE ----- //

  const [hovered, setHovered] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);
  const [preferredPlanKey, setPreferredPlanKey] = useState('');

  // ----- MEMOS ----- //

  const panelBase = useMemo(() => {
    if (activeBase?.key) return activeBase;
    if (!activeBaseKey) return null;
    return bases.find((base) => base?.key === activeBaseKey || base?.id === activeBaseKey) || null;
  }, [activeBase, activeBaseKey, bases]);

  const subtitle = useMemo(() => {
    return 'Billings, MT';
    /*
    const label = String(baseLabel || '').trim();
    if (isPublicBase(activeBase)) {
      return [label, 'Public'].filter(Boolean).join(' · ');
    }
    return label;
    */
  }, [activeBase, baseLabel]);

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- HANDLERS ----- //

  const handleBaseSelect = (base) => {
    setPopupOpen(false);
    onBaseSelect && onBaseSelect(base);
  };

  const handleSettingsClick = useCallback((_base = null, nextTab = null) => {
    setPopupOpen(false);
    setSettingsInitialTab(nextTab || null);
    setSettingsModalOpen(true);
  }, []);

  const handleInviteClick = useCallback((base) => {
    const nextBase = base || panelBase || null;
    if (!nextBase?.key) return;

    setPopupOpen(false);
    setInviteModalOpen(true);
  }, [panelBase]);

  const handleSettingsModalClose = () => {
    setSettingsModalOpen(false);
    setSettingsInitialTab(null);
    setPreferredPlanKey('');
  };

  const handleInviteModalClose = () => {
    setInviteModalOpen(false);
  };

  const handleToggleClick = () => {
    setPopupOpen((prev) => !prev);
    if (onClick) onClick();
  };

  // ----- EFFECTS ----- //

  // Marketing / pricing deep link: /?plan=… → User Settings → Product Lines
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const params = new URLSearchParams(window.location.search);
    const planKey = String(params.get('plan') || '').trim();
    if (!planKey) return undefined;

    openBasePlanSettings({ planKey });

    params.delete('plan');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const params = new URLSearchParams(window.location.search);
    const qboStatus = String(params.get('qbo') || '').trim();
    if (!qboStatus) return undefined;

    openBaseSettings('financials');
    if (qboStatus === 'error') {
      const reason = String(params.get('reason') || '').trim()
        || 'QuickBooks connect did not finish.';
      window.setTimeout(() => window.alert(reason), 0);
    }

    params.delete('qbo');
    params.delete('reason');
    params.delete('baseKey');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
    return undefined;
  }, []);

  useEffect(() => {
    return subscribeOpenBasePlanSettings((detail) => {
      setPreferredPlanKey(String(detail?.planKey || '').trim());
      handleSettingsClick(null, SETTINGS_TAB_PLAN);
    });
  }, [handleSettingsClick]);

  useEffect(() => {
    return subscribeOpenBaseSettings((detail) => {
      const nextTab = String(detail?.tab || '').trim() || null;
      handleSettingsClick(null, nextTab);
    });
  }, [handleSettingsClick]);

  useEffect(() => {
    return subscribeOpenBaseInvite(() => {
      handleInviteClick(null);
    });
  }, [handleInviteClick]);

  // Temporarily interactive on desktop too (base-rail removal experiment).
  const isInteractive = true;
  const showHover = isInteractive && hovered;

  // ----- RENDER ----- //

  return (
    <>
      <div
        className={[
          'switcher-toggle-container',
          isInteractive ? '' : 'switcher-toggle-container--static',
          compact ? 'is-rail-compact' : '',
        ].filter(Boolean).join(' ')}
        style={{
          backgroundColor: showHover ? theme.buttonHoverBackgroundColor : 'transparent',
        }}
        ref={anchorRef}
        title={compact ? (activeBase?.title || 'Switch base') : undefined}
        onClick={isInteractive ? handleToggleClick : undefined}
        onMouseEnter={isInteractive ? () => setHovered(true) : undefined}
        onMouseLeave={isInteractive ? () => setHovered(false) : undefined}
      >
        <div className="switcher-toggle-photo">
          <BaseCover
            base={activeBase}
            borderWidth={0}
            borderRadius={8}
            size={36}
            shape={PhotoShape.SQUARE}
            assetType="switcher"
          />
        </div>

        {!compact ? (
          <div className="switcher-toggle-text-wrapper">
            <div className="switcher-toggle-title-wrapper">
              <div
                className="switcher-toggle-title"
                style={{
                  color: showHover ? theme.buttonHoverLabelColor : theme.foreground,
                }}
              >
                {activeBase?.title || ''}
              </div>
            </div>

            {subtitle ? (
              <div
                className="switcher-toggle-label"
                style={{
                  color: showHover ? theme.buttonHoverLabelColor : theme.foregroundMuted,
                }}
              >
                {subtitle}
              </div>
            ) : null}
          </div>
        ) : null}

        {isInteractive && !compact ? (
          <div className="switcher-toggle-arrow">
            <Icon
              iconKey="keyboard_arrow_down"
              size={16}
              color={showHover ? theme.buttonHoverLabelColor : theme.foreground}
            />
          </div>
        ) : null}
      </div>

      {isInteractive && (
        <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
          <SwitcherPopup
            bases={bases}
            activeBaseKey={activeBaseKey}
            onBaseAdd={onBaseAdd}
            onBaseSelect={handleBaseSelect}
          />
        </Popup>
      )}

      <InviteModal
        isOpen={inviteModalOpen}
        setOpen={setInviteModalOpen}
        base={panelBase}
        onClose={handleInviteModalClose}
      />

      <SettingsModal
        isOpen={settingsModalOpen}
        setOpen={setSettingsModalOpen}
        initialTab={settingsInitialTab}
        preferredPlanKey={preferredPlanKey}
        base={panelBase}
        onClose={handleSettingsModalClose}
        onInviteClick={handleInviteClick}
      />
    </>
  );
});

SwitcherToggle.displayName = 'SwitcherToggle';

export default SwitcherToggle;
